import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import OptimizedImage from "@/components/OptimizedImage";
import { useSignIn } from "@/hooks/useSignIn";

interface WritingExamplesProps {
  onSignIn: () => void;
}

const WritingExamples: React.FC<WritingExamplesProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  
  const articles = [
    {
      title: "15 Technical SEO Checklist: Boost Your Rankings in 2025",
      category: "SEO Guide",
      excerpt: "Technical SEO forms the foundation of every successful search engine optimization strategy. From site speed optimization to structured data markup, these technical elements directly impact how search engines crawl, index, and rank your content. This comprehensive guide covers the essential technical SEO factors that separate top-ranking websites from the competition...",
      featuredImage: "https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/15%20technical%20seo%20checlist%20.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzLzE1IHRlY2huaWNhbCBzZW8gY2hlY2xpc3QgLmpwZyIsImlhdCI6MTc2NDA4NzQyMSwiZXhwIjoxOTIxNzY3NDIxfQ.s5UMkxG1Enu7PUKWPIm-LGMwa8XmgUvZwQdPmQrKCFQ"
    },
    {
      title: "How to Build High-Quality Backlinks: A Complete Strategy Guide",
      category: "Link Building",
      excerpt: "Backlinks remain one of the most powerful ranking factors in Google's algorithm. But not all links are created equal. This in-depth guide reveals proven strategies for earning authoritative backlinks that actually move the needle on your search rankings. Learn how to identify link opportunities, create linkable assets, and build relationships that result in sustainable link growth...",
      featuredImage: "https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/strategic%20backlinks%20image.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL3N0cmF0ZWdpYyBiYWNrbGlua3MgaW1hZ2UuanBnIiwiaWF0IjoxNzY0MDg3NTQ2LCJleHAiOjE5MjE3Njc1NDZ9.cd55f8nPmYPK2bkTpBTHGd14DPfptyHkxT735AmJo2U"
    },
    {
      title: "Keyword Research Mastery: Find High-Value Opportunities Your Competitors Miss",
      category: "SEO Strategy",
      excerpt: "Effective keyword research is the cornerstone of any successful SEO campaign. While your competitors focus on obvious high-volume terms, smart SEO professionals uncover hidden opportunities with better conversion potential and less competition. Discover advanced keyword research techniques, tools, and frameworks that help you identify the exact search terms your target audience uses when they're ready to buy...",
      featuredImage: "https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/keyword%20reserach%20banner.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2tleXdvcmQgcmVzZXJhY2ggYmFubmVyLmpwZyIsImlhdCI6MTc2NDA4NzUxOCwiZXhwIjoxOTIxNzY3NTE4fQ.QCh7qGku665wI5V6d7lmvyQPUydh4N__HwcLNHu1SyQ"
    }
  ];

  return (
    <section id="writing-examples" className="w-full py-16 sm:py-20 lg:py-24 bg-muted/20 scroll-mt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Writing Examples
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              See real examples of content that ranks. Our AI writes articles that read naturally, engage readers, and drive results—just like a professional SEO writer would.
            </p>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 mb-16">
            {articles.map((article, index) => (
              <div
                key={index}
                className="bg-background rounded-2xl overflow-hidden shadow-lg border border-border/50 transition-all duration-300"
              >
                {/* Featured Image */}
                <div className="aspect-video w-full bg-gradient-to-br from-primary/10 to-primary/5 relative overflow-hidden">
                  <OptimizedImage
                    src={article.featuredImage}
                    alt={article.title}
                    width={600}
                    height={338}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 line-clamp-3 leading-relaxed">
                    {article.excerpt}
                  </p>

                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <Button
              onClick={handleClick}
              disabled={signingIn}
              size="lg"
              className="w-full sm:w-auto rounded-full text-lg px-8 py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg min-h-[60px] relative overflow-hidden group mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
              <span className="relative z-10 font-semibold tracking-wide">Start Creating Content Like This</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WritingExamples;
